/**
 * Query-building for the SiteHome finding-tool widgets (recently added /
 * recently updated / current+total+recent counts).
 *
 * DATE FIELD: every widget here reads `issue_date` -- the date DOE issued the
 * document. It is the only date on these objects that means anything to a
 * visitor, and the only one DOE is compared against. The two alternatives are
 * both bookkeeping, and using them was a real defect until 2026-08-03:
 *   `created`   is when the object entered OUR ZODB at migration. The old
 *               month counter used it under a label saying "Updated", so
 *               directives read 0 while the archive was not idle.
 *   `effective` is Plone's portal posting date. Measured 2026-08-03, its
 *               median distance from `issue_date` is 3,697 days on standards
 *               and 1,574 on nnsa (max 16,132) -- it is not a synonym.
 * `issue_date` is a DateIndex, a metadata column, and registered
 * enabled+sortable in plone.app.querystring on all three sites [verified
 * 2026-08-03]. It is populated on 2,726 of 2,749 published documents; the 23
 * without one are absent from these widgets by design, since a document with
 * no issue date has no place in a list ordered by issue date.
 *
 * Shared by asyncData.js (SSR, via the asyncPropsExtenders hook) and
 * SiteHome.jsx (client-side fallback dispatch) so both fire the exact same
 * queries against Volto's stock `searchContent` action (classic @search,
 * NOT @querystring-search — this addon's search/listing BLOCKS use
 * @querystring-search via withNormalizedData/withPresetData, but a plain
 * home widget has no block config to normalize, so it talks to @search
 * directly, same as core's own Search.jsx `dispatch(searchContent('', ...))`
 * pagination call).
 *
 * Site-relative: this Volto instance is already scoped to exactly one
 * Plone site's REST API root (seamless mode, RAZZLE_INTERNAL_API_PATH — see
 * siteConfig/branding/index.ts), so `searchContent('', options, key)` hits
 * `<this site's api root>/@search` — no `path` criterion needed to keep
 * results inside the site.
 */
import { getPreset } from '../../presets';

/**
 * Base portal_type per site for the canonical "documents" family container
 * (backend/scripts/audit/generate_canonical_browse_blocks.py FAMILIES,
 * verified 2026-07-14: standards=Standard, directives=[Directive,
 * OtherPolicy], nnsa=Policy). NOT exported from presets.ts by design — a
 * preset only ever narrows a family, it never restates the family's base
 * portal_type — so it is mirrored here rather than imported.
 */
export const DOCUMENT_PTYPES = {
  standards: ['Standard'],
  directives: ['Directive', 'OtherPolicy'],
  nnsa: ['Policy'],
};

/**
 * documents-family preset ids (see ../../presets) whose `status` criterion
 * defines "current" vs "archived/superseded/cancelled" per site. Reusing
 * the real presets (instead of re-guessing status vocabularies) keeps this
 * widget in lockstep with the canonical browse pages and IS the source for
 * the "Superseded & cancelled →" deep link target.
 */
export const CURRENT_PRESET_ID = {
  standards: 'standards-browse',
  directives: 'directives-browse',
  nnsa: 'nnsa-directives-browse',
};
export const ARCHIVED_PRESET_ID = {
  standards: 'archives-browse',
  directives: 'browse-archived-directives',
  nnsa: 'nnsa-archived-directives',
};

/** Pull the `status` criterion values off a documents-family preset. */
function statusValues(siteId, presetId, fallback) {
  const preset = getPreset(siteId, 'documents', presetId);
  const crit = preset?.criteria?.find((c) => c.i === 'status');
  if (!crit) return fallback;
  return Array.isArray(crit.v) ? crit.v : [crit.v];
}

/**
 * `days` days before today, UTC, as YYYY-MM-DD.
 *
 * A rolling window, deliberately, not a calendar month: on the 1st a
 * calendar-month counter resets to 0 and the homepage claims the archive is
 * empty of recent work. `issue_date` has no relative-date operation
 * registered (its `operations` list carries between/lessThan/largerThan/
 * afterToday/beforeToday, but not largerThanRelativeDate -- verified
 * 2026-08-03), so the boundary is computed here as an absolute date and sent
 * through the plain `largerThan` that `.range: 'min'` maps to.
 */
function isoDaysAgo(days) {
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}

/**
 * Preset id (see ../../presets) for the documents-family listing ordered by
 * acquisition date. Used by SiteHome to give every newly added document a
 * shallow inbound internal link -- see the preset's own comment for why
 * sitemap presence alone was not enough for Google.
 */
export const RECENT_ADDED_PRESET_ID = 'recently-added';

export const RECENT_DAYS = 30;

export const SUBREQUEST_NAMES = [
  'added',
  'updated',
  'current',
  'total',
  'recent',
];

/**
 * Build the 5 `searchContent` option objects (added/updated/current/total/
 * recent) for a given site id. `current`/`total`/`recent` are cheap b_size:1
 * count-only queries (read `items_total` off the subrequest); `added`/
 * `updated` are the two 3-row listing queries rendered through
 * TableListingView.
 */
export function buildHomeQueries(siteId) {
  const ptype = DOCUMENT_PTYPES[siteId] || DOCUMENT_PTYPES.standards;
  const currentStatus = statusValues(siteId, CURRENT_PRESET_ID[siteId], ['Current']);
  const archivedStatus = statusValues(siteId, ARCHIVED_PRESET_ID[siteId], ['Archive']);

  return {
    // Recently added: current-status documents, newest by the date DOE
    // issued them. Sorted on `issue_date` rather than `effective` so this
    // table and the "issued in the last N days" stat below are answering the
    // same question -- they were not, and that is why the count could report
    // documents this table never showed.
    added: {
      portal_type: ptype,
      status: currentStatus,
      sort_on: 'issue_date',
      sort_order: 'descending',
      b_size: 3,
      metadata_fields: '_all',
    },
    // Recently superseded & cancelled: archived documents, newest by the
    // date DOE actually retired them (`superseded_date`, backfilled from the
    // incoming supersedes/cancels relation — the successor or cancellation
    // memo dates the event). NOT `modified`: that is this stack's own
    // bookkeeping timestamp (migration/bulk-op dates), verified 2026-07-30
    // to never hold a real DOE date. Docs with no known retirement date are
    // absent from this sort index and simply cannot surface here.
    updated: {
      portal_type: ptype,
      status: archivedStatus,
      sort_on: 'superseded_date',
      sort_order: 'descending',
      b_size: 3,
      metadata_fields: '_all',
    },
    current: { portal_type: ptype, status: currentStatus, b_size: 1 },
    total: { portal_type: ptype, b_size: 1 },
    recent: {
      portal_type: ptype,
      'issue_date.query': isoDaysAgo(RECENT_DAYS),
      'issue_date.range': 'min',
      b_size: 1,
    },
  };
}

export default buildHomeQueries;
